// --- External Imports ---
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    List as Listcon,
    Save as SaveIcon,
    Crown as CrownIcon,
    X as XIcon,
    Plus as PlusIcon,
    Minus as MinusIcon,
    SquarePen as SquarePenIcon,
    RotateCcwKey as RotateCcwKeyIcon,
    Trash2 as Trash2Icon,
    Info as InfoIcon,
    Star as StarIcon,
} from 'lucide-react';

// --- Services ---
import {
    fetchSmsNumbers,
    fetchWebcallNumbers,
    getEncryptedSSOData,
    fetchAvailableUsers,
    createNewUserConfiguration,
    deleteUserConfiguration,
} from '@/services/ghlService';

// --- Store Actions & utils---
import {
    setSmsNumbersData,
    setWebcallNumbersData,
} from '@/store/numberManagementSlice';
import { setAvailableUsersData } from '@/store/userManagementSlice';
import { showToast } from '@/utils/toast/toast';

// --- Components ---
import Card from '@/components/Card/Card';
import Button from '@/components/Button/Button';
import Select from '@/components/Select/Select';
import DataTable from '@/components/DataTable/DataTable';
import Modal from '@/components/Modal/Modal';
import Chip from '@/components/Chip/Chip';
import SmartTooltip from '@/components/Tooltip/SmartTooltip';
import ErrorMessage from '@/components/ErrorMessage/ErrorMessage';
import { resetSpecificSlices } from '@/store/storeUtils';

function UserManagement() {
    // --- State: Loading ---
    const [loading, setLoading] = useState(false);
    const [numbersLoading, setNumbersLoading] = useState(false);
    const [configLoading, setConfigLoading] = useState(false);

    // --- State: Redux ---
    const dispatch = useDispatch();
    const userState = useSelector((state) => state.user);
    const { userData } = userState;
    const numberManagementState = useSelector(
        (state) => state.numberManagement
    );
    const availableUsersState = useSelector((state) => state.availableUsers);

    // --- State: Users ---
    const [unauthenticatedUsers, setUnauthenticatedUsers] = useState([]);
    const [authenticatedUsers, setAuthenticatedUsers] = useState([]);
    const [targetUser, setTargetUser] = useState({});

    // --- State: Form & Modals ---
    const initialFormData = {
        selectedUser: {},
        selectedSmsNumbers: [],
        selectedSmsDefault: '',
        selectedWebcallNumbers: [],
        selectedWebcallDefault: '',
    };
    const [formData, setFormData] = useState(initialFormData);
    const [showEditingForm, setShowEditingForm] = useState(false);
    const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showNumbersListModal, setShowNumbersListModal] = useState(false);
    const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);

    // --- Pagination & Sorting ---
    const pageSizes = [10, 20, 50, 100];
    const [pageSize, setPageSize] = useState(pageSizes[0]);
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState({
        accessor: 'name',
        direction: 'desc',
    });

    // --- Local Error Variables ---
    const [smsError, setSmsError] = useState(null);
    const [webcallError, setWebcallError] = useState(null);

    // --- Async Helper (for fetching numbers) ---
    async function fetchAndHandleNumbers(
        fetchFn,
        dispatchFn,
        encCodeURI,
        type
    ) {
        try {
            const response = await fetchFn(numberManagementState, encCodeURI);

            if (response.status === 200) {
                dispatch(dispatchFn(response.data));
            } else {
                dispatch(dispatchFn('Not Available'));
                handleErrors(response, type); // Handle 204 status code
            }
        } catch (error) {
            handleErrors(error, type); // Handle 404 status code
        }
    }

    // --- Effects ---
    useEffect(() => {
        if (!userData) {
            showToast('There is no user data available', 'error', 3000);
            return;
        }

        const loadAll = async () => {
            setLoading(true);

            try {
                const encCode = await getEncryptedSSOData();
                const encCodeURI = encodeURIComponent(encCode);

                const availableUsersResult = await fetchAvailableUsers(
                    availableUsersState,
                    encCodeURI
                );

                handleAvailableUsers(availableUsersResult);
            } catch (error) {
                showToast('Failed to fetch available users', 'error', 5000);
                console.error('Failed to fetch available users:', error);
            } finally {
                setLoading(false);
            }
        };
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Local Error Handling ---
    function handleErrors(res, type) {
        if (type === 'sms') {
            setSmsError(res);
        } else if (type === 'webcall') {
            setWebcallError(res);
        }

        console.error('Failed to fetch data for:', type, res);
    }

    // --- Utility Helpers ---
    const isCurrentUserAParent = useMemo(() => {
        const allUsers = [...authenticatedUsers, ...unauthenticatedUsers];
        const currentUserInApiResponse = allUsers.find(
            (user) => user.id === userData.userId
        );

        return currentUserInApiResponse?.authType === 'parent';
    }, [userData, authenticatedUsers, unauthenticatedUsers]);

    function handleAvailableUsers(apiResponse) {
        if (!apiResponse) {
            console.error('There is no API response');
            return;
        }

        if (apiResponse.status === 200) {
            const clonedArr = apiResponse.data.users.map((user) => ({
                ...user,
            }));
            dispatch(setAvailableUsersData(clonedArr));
            const { authenticated, unauthenticated } =
                apiResponse.data.users.reduce(
                    (acc, user) => {
                        if (user.isAuthenticated) {
                            acc.authenticated.push(user);
                        } else {
                            acc.unauthenticated.push(user);
                        }
                        return acc;
                    },
                    {
                        authenticated: [],
                        unauthenticated: [],
                    }
                );

            setAuthenticatedUsers(authenticated);
            setUnauthenticatedUsers(unauthenticated);
        }
    }

    function showValidUsername(item) {
        if (item.id === userData.userId) return userData.userName;
        if (item.name) return item.name;
        return item.id;
    }

    function isChildOfCurrentUser(item) {
        return item?.parentDetails?.id === userData?.userId;
    }

    function isParentUser(item) {
        return item.isAuthenticated && item.authType === 'parent';
    }

    function hasChildren(user) {
        const userChildren = authenticatedUsers.filter(
            (item) => item?.parentDetails?.id === user.id
        );

        if (userChildren.length) {
            user._childNumbers = userChildren.length;
        }

        return Boolean(userChildren.length);
    }

    function numbersCount(user, numberType) {
        const target = user.numbers?.[numberType];

        if (!target) return 0;

        const listLength = target.list?.length ?? 0;

        if (target.default === '') return listLength;

        return target.list.some((item) => item.bnum === target.default)
            ? listLength
            : listLength + 1;
    }

    function hasAnyNumbers(user, type) {
        const data = user?.numbers?.[type];
        if (!data) return false;

        return (
            (Array.isArray(data.list) && data.list.length > 0) ||
            (typeof data.default === 'string' && data.default.trim() !== '')
        );
    }

    // --- Action Handlers (Edit, Save, Delete, Cancel, etc.) ---
    function prepareEditForm(user) {
        setTargetUser(user);

        if (isChildOfCurrentUser(user)) {
            setFormData({
                selectedUser: user,
                selectedSmsNumbers: user?.numbers?.sms?.list,
                selectedSmsDefault: user?.numbers?.sms?.default,
                selectedWebcallNumbers: user?.numbers?.webcall?.list,
                selectedWebcallDefault: user?.numbers?.webcall?.default,
            });

            setShowEditingForm(true);
        } else {
            setFormData(initialFormData);
            setShowEditingForm(false);
            setShowOverrideConfirm(true);
        }
    }

    function handleOverrideConfirmation() {
        setFormData({
            ...initialFormData,
            selectedUser: targetUser,
        });

        setShowEditingForm(true);
        setShowOverrideConfirm(false);
    }

    function prepareDelete(user) {
        setTargetUser(user);
        setShowDeleteConfirm(true);
    }

    async function handleDeleteConfirmation() {
        setLoading(true);
        setShowDeleteConfirm(false);

        const encCode = await getEncryptedSSOData();

        const encCodeURI = encodeURIComponent(encCode);

        try {
            const deleteResponse = await deleteUserConfiguration({
                encryptedSSOData: encCodeURI,
                deletedUserId: targetUser.id,
            });

            if (deleteResponse.status === 200) {
                showToast(
                    deleteResponse?.data?.message ||
                        'User Deleted Successfully',
                    'success',
                    5000
                );

                resetSpecificSlices(dispatch, ['numberManagement']);
            }

            const availableUsersResponse = await fetchAvailableUsers(
                availableUsersState,
                encCodeURI,
                true
            ).catch((err) => {
                showToast('Failed to fetch available users', 'error', 5000);
                console.error('Failed to fetch available users:', err);
                setLoading(false);
                return null;
            });

            handleAvailableUsers(availableUsersResponse);
        } finally {
            setLoading(false);
        }
    }

    function handleCancelConfiguration() {
        setFormData(initialFormData);
        setTargetUser({});
        setShowEditingForm(false);
    }

    async function handleSaveConfiguration(config) {
        setConfigLoading(true);

        const encCode = await getEncryptedSSOData();

        const encCodeURI = encodeURIComponent(encCode);

        await createNewUserConfiguration(config)
            .then((response) => {
                if (response.status === 200) {
                    showToast(
                        response?.data?.message || 'User Created Successfully',
                        'success',
                        5000
                    );

                    resetSpecificSlices(dispatch, ['numberManagement']);
                }
            })
            .catch((err) => {
                showToast('Failed to create configuration', 'error', 5000);
                console.error('Failed to create configuration:', err);
                setConfigLoading(false);
                return;
            });

        setConfigLoading(false);
        handleCancelConfiguration();

        setLoading(true);
        const availableUsersResponse = await fetchAvailableUsers(
            availableUsersState,
            encCodeURI,
            true
        ).catch((err) => {
            showToast('Failed to fetch available users', 'error', 5000);
            console.error('Failed to fetch available users:', err);
            setLoading(false);
            return null;
        });
        setLoading(false);

        handleAvailableUsers(availableUsersResponse);
    }

    async function handleAddNewConfiguration() {
        setFormData(initialFormData);
        setTargetUser({});

        if (showEditingForm && !formData.selectedUser.id) {
            setShowEditingForm(false);
        } else {
            setNumbersLoading(true);
            const encCode = await getEncryptedSSOData();
            const encCodeURI = encodeURIComponent(encCode);

            const results = await Promise.allSettled([
                fetchAndHandleNumbers(
                    fetchSmsNumbers,
                    setSmsNumbersData,
                    encCodeURI,
                    'sms'
                ),
                fetchAndHandleNumbers(
                    fetchWebcallNumbers,
                    setWebcallNumbersData,
                    encCodeURI,
                    'webcall'
                ),
            ]);

            const smsResult = results[0];
            const webcallResult = results[1];

            if (smsResult.status === 'rejected') {
                handleErrors(smsResult, 'sms');
            }

            if (webcallResult.status === 'rejected') {
                handleErrors(webcallResult, 'webcall');
            }

            setNumbersLoading(false);
            setShowEditingForm(true);
        }
    }

    function handleOpenNumbersListModal(user) {
        setTargetUser(user);
        setShowNumbersListModal(true);
    }

    function handleCloseNumbersListModal() {
        setTargetUser({});
        setShowNumbersListModal(false);
    }

    function handleOpenUserDetailsModal(user) {
        setTargetUser(user);
        setShowUserDetailsModal(true);
    }

    function handleCloseUserDetailsModal() {
        setTargetUser({});
        setShowUserDetailsModal(false);
    }

    // --- Column Definitions ---
    function DefaultChip({ user, type }) {
        const target = user.numbers?.[type];

        let label = '---';

        if (target?.default) {
            const targetInList = target.list.find(
                (item) => item.bnum === target.default
            );

            label = `${target.default} (${targetInList?.adName || '---'})`;
        }

        return label === '---' ? label : <Chip label={label} />;
    }

    const cols = [
        {
            label: 'User',
            accessor: 'name',
            sortable: true,
            render: (row) => {
                return (
                    <div className="flex flex-col">
                        <div className="flex items-center gap-1">
                            <span className="font-medium text-secondary-900">
                                {showValidUsername(row)}
                            </span>

                            {isParentUser(row) && (
                                <SmartTooltip content="Parent User">
                                    <CrownIcon color="#fb923c" size={18} />
                                </SmartTooltip>
                            )}
                        </div>
                        <span className="text-sm text-secondary-500">
                            {row.id === userData.userId
                                ? userData.email
                                : row.email || '---'}
                        </span>
                    </div>
                );
            },
        },
        {
            label: 'SMS',
            accessor: 'smsNumbers',
            sortable: false,
            render: (row) => {
                if (!hasAnyNumbers(row, 'sms')) return null;

                return (
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-1">
                            <DefaultChip user={row} type="sms" />

                            {row.numbers?.sms?.default && (
                                <SmartTooltip content="Default">
                                    <StarIcon
                                        size={12}
                                        className="text-yellow-400 fill-yellow-400"
                                    />
                                </SmartTooltip>
                            )}
                        </div>

                        <div className="text-xs text-secondary font-medium">
                            Number Count: {numbersCount(row, 'sms')}
                        </div>
                    </div>
                );
            },
        },
        {
            label: 'VOICE',
            accessor: 'webcallNumbers',
            sortable: false,
            render: (row) => {
                if (!hasAnyNumbers(row, 'webcall')) return null;

                return (
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-1">
                            <DefaultChip user={row} type="webcall" />

                            {row.numbers?.webcall?.default && (
                                <SmartTooltip content="Default">
                                    <StarIcon
                                        size={12}
                                        className="text-yellow-400 fill-yellow-400"
                                    />
                                </SmartTooltip>
                            )}
                        </div>

                        <div className="text-xs text-secondary font-medium">
                            Number Count: {numbersCount(row, 'webcall')}
                        </div>
                    </div>
                );
            },
        },
    ];

    // --- Nested Components ---
    function AdminAddButton() {
        if (loading || numbersLoading) {
            return (
                <div>
                    <Button variant="success" loading>
                        Number Assignment
                    </Button>
                </div>
            );
        }

        if (isCurrentUserAParent) {
            return (
                <div>
                    <Button
                        variant={showEditingForm ? 'secondary' : 'success'}
                        loading={loading || numbersLoading}
                        onClick={handleAddNewConfiguration}
                        icon={showEditingForm ? MinusIcon : PlusIcon}
                    >
                        Number Assignment
                    </Button>
                </div>
            );
        }

        return (
            <SmartTooltip
                content={
                    <div className="flex flex-col">
                        <div>
                            User does not have the permission to assign numbers.
                        </div>

                        <div>
                            <span>Kindly </span>
                            <a
                                href="mailto:support@avanser.com"
                                className="underline font-medium hover:text-danger-600"
                            >
                                contact support
                            </a>
                            <span> for more details.</span>
                        </div>
                    </div>
                }
            >
                <Button variant="success" icon={PlusIcon} disabled>
                    Number Assignment
                </Button>
            </SmartTooltip>
        );
    }

    function ConfigurationForm({
        formData,
        store,
        formLoading,
        onSave,
        onCancel,
    }) {
        const [selectedUser, setSelectedUser] = useState(formData.selectedUser);
        const [selectedSmsNumbers, setSelectedSmsNumbers] = useState(
            formData.selectedSmsNumbers
        );
        const [selectedSmsDefault, setSelectedSmsDefault] = useState(
            formData.selectedSmsDefault
        );
        const [selectedWebcallNumbers, setSelectedWebcallNumbers] = useState(
            formData.selectedWebcallNumbers
        );
        const [selectedWebcallDefault, setSelectedWebcallDefault] = useState(
            formData.selectedWebcallDefault
        );

        const smsNumbersList =
            store?.sms === 'Not Available' ? [] : store?.sms?.bnum_list;

        const webcallNumbersList =
            store?.webcall === 'Not Available' ? [] : store?.webcall?.bnum_list;

        const isFormValid =
            Boolean(selectedUser?.id) &&
            (selectedSmsNumbers.length > 0 ||
                selectedWebcallNumbers.length > 0);

        function calculateDefault(prevDefault, numbers) {
            return numbers.includes(prevDefault) ? prevDefault : '';
        }

        function handleNumberSelection(arr, type) {
            if (type === 'sms') {
                setSelectedSmsNumbers(arr);
                setSelectedSmsDefault(
                    calculateDefault(selectedSmsDefault, arr)
                );
            } else if (type === 'webcall') {
                setSelectedWebcallNumbers(arr);
                setSelectedWebcallDefault(
                    calculateDefault(selectedWebcallDefault, arr)
                );
            }
        }

        function handleDefaultSelection(e, type) {
            let result;

            if (e.target.checked) {
                result = e.target.value;
            } else {
                result = '';
            }

            if (type === 'sms') {
                setSelectedSmsDefault(result);
            } else if (type === 'webcall') {
                setSelectedWebcallDefault(result);
            }
        }

        async function handleSaveProcess() {
            const encCode = await getEncryptedSSOData();

            const result = {
                encryptedSSOData: encCode,
                userDetails: {
                    id: selectedUser.id,
                    username: selectedUser.name,
                    email: selectedUser.email,
                    accessRole: selectedUser.accessRole,
                },
                numbers: {
                    sms: {
                        list: selectedSmsNumbers,
                        default: selectedSmsDefault,
                    },
                    webcall: {
                        list: selectedWebcallNumbers,
                        default: selectedWebcallDefault,
                    },
                },
            };

            onSave(result);
        }

        return (
            <Card className="mt-2">
                <Card.Header divider>
                    <div className="text-lg font-semibold text-gray-900 mb-4">
                        {Boolean(targetUser?.id)
                            ? 'Edit Configuration'
                            : 'Add New Configuration'}
                    </div>
                </Card.Header>

                <div className="grid md:grid-cols-3 gap-4 mb-2">
                    {/* User Section */}
                    <Select
                        label="Select a user"
                        valueKey="id"
                        searchProps={{
                            enabled: true,
                            keyArray: ['name', 'email'],
                        }}
                        inputValue={selectedUser}
                        options={unauthenticatedUsers}
                        loading={configLoading}
                        disabled={
                            Boolean(targetUser.id) &&
                            selectedUser.id === targetUser.id
                        }
                        returnFullObject
                        getLabel={(item) => {
                            if (Object.keys(item).length === 0) return null;

                            return (
                                <div className="flex flex-wrap text-sm">
                                    <span className="text-ellipsis whitespace-nowrap me-1">
                                        {item.name}
                                    </span>
                                    {item.email && (
                                        <span className="text-ellipsis whitespace-nowrap text-secondary-500">
                                            ({item.email})
                                        </span>
                                    )}
                                </div>
                            );
                        }}
                        onChange={(obj) => setSelectedUser(obj)}
                    />

                    <div>
                        {/* SMS Section */}
                        {smsError || store.sms === 'Not Available' ? (
                            smsError?.status === 204 ||
                            store === 'Not Available' ? (
                                <SmartTooltip
                                    content="No numbers allocated for SMS"
                                    containerClassName="w-full"
                                >
                                    <Select
                                        label="SMS Numbers"
                                        inputValue={[]}
                                        options={[]}
                                        loading={loading}
                                        disabled
                                    />
                                </SmartTooltip>
                            ) : (
                                <ErrorMessage />
                            )
                        ) : (
                            <Select
                                label="SMS Numbers"
                                valueKey="bnum"
                                searchProps={{
                                    enabled: true,
                                    keyArray: ['bnum', 'adName'],
                                }}
                                inputValue={selectedSmsNumbers}
                                options={smsNumbersList}
                                loading={loading}
                                multi
                                keepOpenAfterSelect
                                returnFullObject
                                getLabel={(item) => {
                                    return (
                                        <div className="flex flex-wrap items-center">
                                            <span className="me-2 text-nowrap text-sm">
                                                {item.bnum}
                                            </span>
                                            {item.adName ? (
                                                <span className="font-light text-xs text-secondary-500">
                                                    ({item.adName})
                                                </span>
                                            ) : null}
                                        </div>
                                    );
                                }}
                                onChange={(arr) =>
                                    handleNumberSelection(arr, 'sms')
                                }
                            />
                        )}

                        {/* SMS Default Section */}
                        {selectedSmsNumbers.length > 0 && (
                            <div className="mt-3">
                                <label className="block text-xs mb-1">
                                    Default SMS Number
                                </label>

                                <div className="ms-3 max-h-[100px] overflow-auto pretty-scrollbar">
                                    {selectedSmsNumbers.map(
                                        ({ bnum, adName }) => (
                                            <label
                                                key={bnum}
                                                className="flex items-center mb-2 w-fit"
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="smsDefaultInput"
                                                    value={bnum}
                                                    checked={
                                                        selectedSmsDefault ===
                                                        bnum
                                                    }
                                                    onChange={(e) =>
                                                        handleDefaultSelection(
                                                            e,
                                                            'sms'
                                                        )
                                                    }
                                                    className="mr-2 text-green-600 focus:ring-green-500"
                                                />

                                                <span className="text-sm flex flex-wrap">
                                                    {bnum}
                                                    <span className="font-light text-xs text-secondary-500 ms-1">
                                                        ({adName})
                                                    </span>
                                                </span>
                                            </label>
                                        )
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div>
                        {/* Webcall Section */}
                        {webcallError || store.webcall === 'Not Available' ? (
                            webcallError?.status === 204 ||
                            store.webcall === 'Not Available' ? (
                                <SmartTooltip
                                    content="No numbers allocated for Voice"
                                    containerClassName="w-full"
                                >
                                    <Select
                                        label="Voice Numbers"
                                        inputValue={[]}
                                        options={[]}
                                        loading={loading}
                                        disabled
                                    />
                                </SmartTooltip>
                            ) : (
                                <ErrorMessage />
                            )
                        ) : (
                            <Select
                                label="Voice Numbers"
                                valueKey="bnum"
                                searchProps={{
                                    enabled: true,
                                    keyArray: ['bnum', 'adName'],
                                }}
                                inputValue={selectedWebcallNumbers}
                                options={webcallNumbersList}
                                loading={loading}
                                multi
                                keepOpenAfterSelect
                                returnFullObject
                                getLabel={(item) => (
                                    <div className="flex flex-wrap items-center">
                                        <span className="me-2 text-nowrap text-sm">
                                            {item.bnum}
                                        </span>
                                        {item.adName ? (
                                            <span className="font-light text-xs text-secondary-500">
                                                ({item.adName})
                                            </span>
                                        ) : null}
                                    </div>
                                )}
                                onChange={(arr) =>
                                    setSelectedWebcallNumbers(arr)
                                }
                            />
                        )}

                        {/* Webcall Default Section */}
                        {selectedWebcallNumbers.length > 0 && (
                            <div className="mt-3">
                                <label className="block text-xs mb-1">
                                    Default Voice Number
                                </label>

                                <div className="ms-3 max-h-[100px] overflow-auto pretty-scrollbar">
                                    {selectedWebcallNumbers.map(
                                        ({ bnum, adName }) => (
                                            <label
                                                key={bnum}
                                                className="flex items-center mb-2 w-fit"
                                            >
                                                <input
                                                    type="checkbox"
                                                    name="webcallDefaultInput"
                                                    value={bnum}
                                                    checked={
                                                        selectedWebcallDefault ===
                                                        bnum
                                                    }
                                                    onChange={(e) =>
                                                        handleDefaultSelection(
                                                            e,
                                                            'webcall'
                                                        )
                                                    }
                                                    className="mr-2 text-green-600 focus:ring-green-500"
                                                />

                                                <span className="text-sm flex flex-wrap">
                                                    {bnum}
                                                    <span className="font-light text-xs text-secondary-500 ms-1">
                                                        ({adName})
                                                    </span>
                                                </span>
                                            </label>
                                        )
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <Card.Footer divider>
                    <div className="flex items-center justify-end gap-4">
                        <Button
                            variant="secondary"
                            icon={XIcon}
                            onClick={onCancel}
                            disabled={formLoading}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="primary"
                            icon={SaveIcon}
                            onClick={handleSaveProcess}
                            disabled={!isFormValid}
                            loading={formLoading}
                        >
                            Save Configurations
                        </Button>
                    </div>
                </Card.Footer>
            </Card>
        );
    }

    function AuthenticatedUsersList({
        data,
        columns,
        page,
        setPage,
        pageSize,
        setPageSize,
        sort,
        onSortChange,
        listLoading,
    }) {
        function getOverrideTooltipTitle(row, loggedInUser) {
            if (row.id === loggedInUser.userId) {
                return 'User does not have the permission to edit their own numbers.';
            }

            if (hasChildren(row)) {
                return `This user has ${row._childNumbers} accounts under their ownership`;
            }

            if (!isCurrentUserAParent) {
                return (
                    <div className="flex flex-col">
                        <div>
                            User does not have the permission to override
                            numbers.
                        </div>

                        <div>
                            <span>Kindly </span>
                            <a
                                href="mailto:support@avanser.com"
                                className="underline font-medium hover:text-danger-600"
                            >
                                contact support
                            </a>
                            <span> for more details.</span>
                        </div>
                    </div>
                );
            } else {
                return isChildOfCurrentUser(row) ? 'Edit' : 'Override';
            }
        }

        return (
            <Card className="mt-4">
                <Card.Header>
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">
                            All Configured Users
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            All users with SMS and/or Voice configurations
                            (visible to all admins)
                        </p>
                    </div>
                </Card.Header>

                <DataTable
                    columns={columns}
                    data={data}
                    loading={listLoading}
                    sort={sort}
                    maxHeight={800}
                    onSortChange={onSortChange}
                    page={page}
                    pageSize={pageSize}
                    pageSizes={pageSizes}
                    onPageChange={setPage}
                    onPageSizeChange={(newSize) => {
                        setPageSize(newSize);
                        setPage(1);
                    }}
                    renderActions={(row) => (
                        <div className="flex flex-wrap gap-2">
                            {hasChildren(row) ? (
                                <>
                                    <SmartTooltip
                                        content={getOverrideTooltipTitle(
                                            row,
                                            userData
                                        )}
                                    >
                                        <Button
                                            variant={
                                                isChildOfCurrentUser(row)
                                                    ? 'info'
                                                    : 'warning'
                                            }
                                            appearance="outline"
                                            icon={
                                                isChildOfCurrentUser(row)
                                                    ? SquarePenIcon
                                                    : RotateCcwKeyIcon
                                            }
                                            size="sm"
                                            disabled
                                        />
                                    </SmartTooltip>

                                    <SmartTooltip
                                        content={
                                            row.id === userData.userId
                                                ? 'User does not have the permission to delete their own account.'
                                                : `This user has ${row._childNumbers} accounts under their ownership`
                                        }
                                    >
                                        <Button
                                            variant="danger"
                                            appearance="outline"
                                            size="sm"
                                            icon={Trash2Icon}
                                            disabled
                                        ></Button>
                                    </SmartTooltip>
                                </>
                            ) : (
                                <>
                                    <SmartTooltip
                                        content={getOverrideTooltipTitle(
                                            row,
                                            userData
                                        )}
                                    >
                                        <Button
                                            variant={`${isChildOfCurrentUser(row) ? 'info' : 'warning'}`}
                                            appearance="outline"
                                            icon={
                                                isChildOfCurrentUser(row)
                                                    ? SquarePenIcon
                                                    : RotateCcwKeyIcon
                                            }
                                            onClick={() => prepareEditForm(row)}
                                            size="sm"
                                            disabled={
                                                row.id === userData.userId ||
                                                !isCurrentUserAParent
                                            }
                                        ></Button>
                                    </SmartTooltip>

                                    <SmartTooltip
                                        content={
                                            row.id === userData.userId
                                                ? 'User does not have the permission to delete their own account.'
                                                : 'Delete'
                                        }
                                    >
                                        <Button
                                            variant="danger"
                                            appearance="outline"
                                            icon={Trash2Icon}
                                            onClick={() => prepareDelete(row)}
                                            size="sm"
                                            disabled={
                                                row.id === userData.userId
                                            }
                                        ></Button>
                                    </SmartTooltip>
                                </>
                            )}

                            <SmartTooltip content="Info">
                                <Button
                                    variant="info"
                                    appearance="outline"
                                    onClick={() =>
                                        handleOpenUserDetailsModal(row)
                                    }
                                    size="sm"
                                    icon={InfoIcon}
                                ></Button>
                            </SmartTooltip>

                            <SmartTooltip content="View Numbers">
                                <Button
                                    variant="success"
                                    appearance="outline"
                                    icon={Listcon}
                                    onClick={() =>
                                        handleOpenNumbersListModal(row)
                                    }
                                    size="sm"
                                    disabled={
                                        !hasAnyNumbers(row, 'sms') &&
                                        !hasAnyNumbers(row, 'webcall')
                                    }
                                ></Button>
                            </SmartTooltip>
                        </div>
                    )}
                />
            </Card>
        );
    }

    function OverrideConfirmModal({
        showOverrideConfirm,
        setShowOverrideConfirm,
        targetUser,
        showValidUsername,
        isParentUser,
        handleOverrideConfirmation,
    }) {
        return (
            <Modal
                width="500px"
                isOpen={showOverrideConfirm}
                onClose={() => setShowOverrideConfirm(false)}
                title="Configuration Override Required"
            >
                <div className="max-h-[600px] flex flex-col gap-4">
                    <div className="flex flex-col gap-4 text-secondary">
                        <div>
                            The user{' '}
                            <span className="font-bold">
                                {showValidUsername(targetUser)}
                            </span>{' '}
                            is already{' '}
                            {isParentUser(targetUser)
                                ? 'a parent user!'
                                : 'configured by:'}
                        </div>
                        <div className="p-2 bg-warning-50 flex flex-col gap-1">
                            <div className="flex gap-4">
                                <span className="font-bold text-warning-900">
                                    {isParentUser(targetUser)
                                        ? showValidUsername(targetUser)
                                        : targetUser.parentDetails?.username ||
                                          '---'}
                                </span>

                                <CrownIcon color="#fb923c" size={20} />
                            </div>

                            <span className="text-warning">
                                {isParentUser(targetUser)
                                    ? targetUser.email
                                    : targetUser.parentDetails?.email}
                            </span>
                        </div>
                        Do you want to override their configuration and assign
                        this user to yourself?
                    </div>

                    <div className="text-warning">
                        Note: Account phone number if configured by this user
                        shall be deleted in the process
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                        <Button
                            appearance="outline"
                            variant="secondary"
                            size="md"
                            onClick={() => setShowOverrideConfirm(false)}
                        >
                            NO
                        </Button>

                        <Button
                            variant="danger"
                            size="md"
                            onClick={handleOverrideConfirmation}
                        >
                            YES, Override
                        </Button>
                    </div>
                </div>
            </Modal>
        );
    }

    function DeleteConfirmModal({
        showDeleteConfirm,
        setShowDeleteConfirm,
        targetUser,
        handleDeleteConfirmation,
    }) {
        const [deleteConfirmText, setDeleteConfirmText] = useState('');

        useEffect(() => {
            if (!showDeleteConfirm) setDeleteConfirmText('');
        }, [showDeleteConfirm]);

        return (
            <Modal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                title={`Delete "${showValidUsername(targetUser)}"`}
            >
                <div className="max-h-[600px] overflow-auto">
                    <div>
                        If you are sure, please type{' '}
                        <span className="text-danger select-none">
                            "DELETE"
                        </span>{' '}
                        in the input field below and press the "Yes" button
                    </div>

                    <div className="flex justify-center pt-2">
                        <input
                            value={deleteConfirmText}
                            type="text"
                            className="border border-danger rounded-md px-3 py-2 text-danger focus:outline-none focus:ring-2 focus:ring-danger focus:border-danger"
                            onChange={(e) =>
                                setDeleteConfirmText(e.target.value)
                            }
                            placeholder='Type "DELETE" here'
                            disabled={loading}
                        />
                    </div>

                    <div className="text-warning mt-2">
                        Note: Account level phone number also shall be deleted,
                        if configured by this user
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                        <Button
                            appearance="outline"
                            variant="secondary"
                            size="md"
                            onClick={() => setShowDeleteConfirm(false)}
                            disabled={configLoading}
                        >
                            NO
                        </Button>

                        <Button
                            variant="danger"
                            size="md"
                            onClick={handleDeleteConfirmation}
                            disabled={deleteConfirmText !== 'DELETE'}
                            loading={configLoading}
                        >
                            YES, Delete
                        </Button>
                    </div>
                </div>
            </Modal>
        );
    }

    function NumbersListModal({ showNumbersListModal, targetUser, onClose }) {
        const sms = targetUser.numbers?.sms;
        const webcall = targetUser.numbers?.webcall;

        const smsNumbersCount = numbersCount(targetUser, 'sms');

        const webcallNumbersCount = numbersCount(targetUser, 'webcall');

        const hasSmsNumbers =
            (sms && sms.list.length !== 0) || sms?.default !== '';

        const hasWebcallNumbers =
            (webcall && webcall.list.length !== 0) || webcall?.default !== '';

        if (!hasSmsNumbers && !hasWebcallNumbers) return null;

        return (
            <Modal
                width="900px"
                isOpen={showNumbersListModal}
                onClose={onClose}
                title={`"${showValidUsername(targetUser)}" Numbers`}
            >
                <div className="max-h-[300px] flex justify-between">
                    {hasSmsNumbers && (
                        <div className="w-2/5 flex flex-col overflow-auto gap-2">
                            <span>SMS Numbers ({smsNumbersCount})</span>

                            <div className="w-100 flex flex-col overflow-auto gap-2">
                                {sms?.default && (
                                    <div className="flex items-center gap-1 text-nowrap">
                                        <DefaultChip
                                            user={targetUser}
                                            type="sms"
                                        />

                                        <SmartTooltip content="Default">
                                            <StarIcon
                                                size={12}
                                                className="text-yellow-400 fill-yellow-400"
                                            />
                                        </SmartTooltip>
                                    </div>
                                )}

                                {sms?.list?.map((item) => {
                                    if (item.bnum !== sms.default) {
                                        return (
                                            <div
                                                key={item.bnum}
                                                className="flex items-center gap-1 text-nowrap"
                                            >
                                                <Chip
                                                    variant="info"
                                                    label={
                                                        `${item.bnum}` +
                                                        ' (' +
                                                        item.adName +
                                                        ')'
                                                    }
                                                />
                                            </div>
                                        );
                                    }

                                    return null;
                                })}
                            </div>
                        </div>
                    )}

                    {hasWebcallNumbers && (
                        <div className="w-2/5 flex flex-col overflow-auto gap-2">
                            <span>Voice Numbers ({webcallNumbersCount})</span>
                            <div className="w-100 flex flex-col overflow-auto gap-2">
                                {webcall?.default && (
                                    <div className="flex items-center gap-1 text-nowrap">
                                        <DefaultChip
                                            user={targetUser}
                                            type="webcall"
                                        />

                                        <SmartTooltip content="Default">
                                            <StarIcon
                                                size={12}
                                                className="text-yellow-400 fill-yellow-400"
                                            />
                                        </SmartTooltip>
                                    </div>
                                )}

                                {webcall?.list?.map((item) => {
                                    if (item.bnum !== webcall.default) {
                                        return (
                                            <div
                                                key={item.bnum}
                                                className="flex items-center gap-1 text-nowrap"
                                            >
                                                <Chip
                                                    variant="info"
                                                    label={
                                                        `${item.bnum}` +
                                                        ' (' +
                                                        item.adName +
                                                        ')'
                                                    }
                                                />
                                            </div>
                                        );
                                    }

                                    return null;
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        );
    }

    function UserDetailsModal({ showUserDetailsModal, targetUser, onClose }) {
        return (
            <Modal
                width="900px"
                isOpen={showUserDetailsModal}
                onClose={onClose}
                title={
                    <div className="flex items-center gap-2">
                        <span>User Details</span>
                        {isParentUser(targetUser) && (
                            <CrownIcon color="#fb923c" size={16} />
                        )}
                    </div>
                }
            >
                <div className="max-h-[500px] flex justify-between gap-2">
                    <div className="w-1/2 flex flex-col px-4 py-2 rounded gap-2">
                        <div className="flex justify-between gap-4">
                            <span className="text-info">User ID:</span>
                            <span className="text-sm">{targetUser.id}</span>
                        </div>

                        {targetUser.name && (
                            <div className="flex justify-between gap-4">
                                <span className="text-info">User Name:</span>
                                <span className="text-sm">
                                    {targetUser.name}
                                </span>
                            </div>
                        )}

                        {targetUser.email && (
                            <div className="flex justify-between gap-4">
                                <span className="text-info">User Email:</span>
                                <span className="text-sm">
                                    {targetUser.email}
                                </span>
                            </div>
                        )}

                        {targetUser.accessRole && (
                            <div className="flex justify-between gap-4">
                                <span className="text-info">User Role:</span>
                                <span className="text-sm">
                                    {targetUser.accessRole}
                                </span>
                            </div>
                        )}
                    </div>

                    {targetUser.parentDetails &&
                        Object.keys(targetUser.parentDetails || {}).length !==
                            0 && (
                            <div className="w-1/2  flex flex-col px-4 py-2 rounded gap-2">
                                {targetUser.parentDetails.username && (
                                    <div className="flex items-center justify-between gap-4">
                                        <span className="text-info">
                                            Parent Name:
                                        </span>
                                        <span className="text-sm">
                                            {targetUser.parentDetails.username}
                                        </span>
                                    </div>
                                )}

                                {targetUser.parentDetails.email && (
                                    <div className="flex items-center justify-between gap-4">
                                        <span className="text-info">
                                            Parent Email:
                                        </span>
                                        <span className="text-sm">
                                            {targetUser.parentDetails.email}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                </div>
            </Modal>
        );
    }

    // --- Render ---
    return (
        <>
            <AdminAddButton />

            {showEditingForm && (
                <ConfigurationForm
                    formData={formData}
                    setFormData={setFormData}
                    store={numberManagementState}
                    formLoading={configLoading}
                    onSave={handleSaveConfiguration}
                    onCancel={handleCancelConfiguration}
                />
            )}

            <AuthenticatedUsersList
                data={authenticatedUsers}
                columns={cols}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                sort={sort}
                onSortChange={setSort}
                listLoading={loading}
            />

            <OverrideConfirmModal
                showOverrideConfirm={showOverrideConfirm}
                setShowOverrideConfirm={setShowOverrideConfirm}
                targetUser={targetUser}
                showValidUsername={showValidUsername}
                isParentUser={isParentUser}
                handleOverrideConfirmation={handleOverrideConfirmation}
            />

            <DeleteConfirmModal
                showDeleteConfirm={showDeleteConfirm}
                setShowDeleteConfirm={setShowDeleteConfirm}
                targetUser={targetUser}
                handleDeleteConfirmation={handleDeleteConfirmation}
            />

            <NumbersListModal
                showNumbersListModal={showNumbersListModal}
                targetUser={targetUser}
                onClose={handleCloseNumbersListModal}
            />

            <UserDetailsModal
                showUserDetailsModal={showUserDetailsModal}
                targetUser={targetUser}
                onClose={handleCloseUserDetailsModal}
            />
        </>
    );
}

export default UserManagement;
